import { motion } from "framer-motion";
import { IMAGES } from "@/assets/images";
import { TimelineCard } from "@/components/Cards";
import { timelineData } from "@/data/index";

export default function Timeline() {
  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary/20">
      {/* Hero Section with Quantum Aesthetic */}
      <section className="relative pt-32 pb-24 px-4 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.PARTICLE_PHYSICS_3} 
            alt="Quantum Energy Field" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />
        </div>
        
        <div className="container mx-auto relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <span className="inline-block px-4 py-1.5 mb-6 text-xs font-mono tracking-widest uppercase bg-primary/10 text-primary border border-primary/20 rounded-full">
              1925 — 2026
            </span>
            <h1 className="text-5xl md:text-8xl font-bold tracking-tight mb-8 font-sans">
              The <span className="text-primary">Quantum</span> <br /> 
              Centennial
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto font-sans leading-relaxed">
              From the probabilistic foundations laid in 1925 to the sovereign quantum initiatives 
              of the Kingdom of Bahrain in 2026. Explore the milestones that redefined our reality.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Interactive Timeline Section */}
      <section className="py-24 px-4 relative">
        {/* Decorative background elements mimicking particle excitation */}
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-accent/5 rounded-full blur-[120px] pointer-events-none" />

        <div className="container mx-auto max-w-6xl">
          <div className="relative">
            {/* Central Vertical Wavefront Line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-linear-to-b from-primary/50 via-border to-accent/50 hidden md:block transform -translate-x-1/2" />
            
            <div className="flex flex-col gap-16 md:gap-32">
              {timelineData.map((milestone, index) => (
                <TimelineCard 
                  key={milestone.id} 
                  milestone={milestone} 
                  index={index} 
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Second Quantum Revolution Callout */}
      <section className="py-32 px-4 border-t border-border/40">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="space-y-4">
                <h2 className="text-3xl md:text-5xl font-bold font-sans tracking-tight leading-tight">
                  Entering the Era of <br />
                  <span className="text-accent">Entangled Innovation</span>
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed font-sans">
                  As we mark 100 years of quantum exploration, Bahrain is positioning itself as a regional leader in the second quantum revolution. By integrating quantum computing into national cybersecurity and drug discovery pipelines, we are not just observing the future—we are computing it.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-card border border-border rounded-xl group hover:border-primary/50 transition-colors">
                  <p className="text-xs font-mono text-primary mb-1 uppercase tracking-tighter">Strategic Focus</p>
                  <p className="font-semibold">National Cyber Security</p>
                </div>
                <div className="p-4 bg-card border border-border rounded-xl group hover:border-accent/50 transition-colors">
                  <p className="text-xs font-mono text-accent mb-1 uppercase tracking-tighter">Strategic Focus</p>
                  <p className="font-semibold">Biotech Acceleration</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute inset-0 bg-primary/10 blur-3xl rounded-full" />
              <div className="relative overflow-hidden rounded-2xl border border-border/50 shadow-2xl aspect-video group">
                <img 
                  src={IMAGES.QUANTUM_COMPUTING_2} 
                  alt="Future Quantum Processor"
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-linear-to-t from-background/80 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="font-mono text-xs text-white/70 mb-1">PROJECTED 2026</p>
                  <p className="text-white font-medium">Bahrain Regional Quantum Hub Deployment</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technical Footer specifier */}
      <div className="py-12 border-t border-border/20 text-center">
        <p className="text-xs font-mono text-muted-foreground uppercase tracking-[0.2em]">
          &copy; 2026 Bahrain Quantum Centennial Initiative — Scientific Observation Log
        </p>
      </div>
    </div>
  );
}
